import clsx from 'clsx'
import React, { CSSProperties, Fragment, useState } from 'react'
import styles from './BestListComparisonFilter.module.css'
import {
  BasicStyledComponent,
  DSBestListComparisonTableFilterSettings,
  DSBestListComparisonTableState,
} from 'shared-definitions/types'
import RWButton from '../buttons/RWButton'
import { useScrollHeight } from '../hooks/use-scroll-height'
import ArrowBird from 'shared-svg/ArrowBottomBird.svg'
import SplitterLine from './SplitterLine'
import FieldBox from '../form/FieldBox'
import { FieldSelect } from '../form/FieldSelect'
import { FieldNumber } from '../form/FieldNumber'
import { FieldSlider } from '../form/FieldSlider'
import { isEqualBestListFilter } from 'shared-code/compare-best-list-filter'

export interface BestListComparisonFilterProps extends BasicStyledComponent {
  settings: DSBestListComparisonTableFilterSettings
  collapsible?: boolean
  onSubmit: (values: DSBestListComparisonTableState) => void
  currentValues: DSBestListComparisonTableState
}

export const BestListComparisonFilter: React.FC<BestListComparisonFilterProps> = ({
  collapsible,
  settings,
  className,
  onSubmit,
  currentValues,
}) => {
  const [collapsed, setCollapsed] = useState(true)
  const [formValues, setFormValues] = useState(currentValues)
  const emptyFormValues = isEqualBestListFilter(formValues, settings.form.defaultState)
  const { ref, height } = useScrollHeight<HTMLFormElement>()

  return (
    <div
      className={clsx(className, styles.container)}
      style={{ '--form-height': `${height}px` } as CSSProperties}
    >
      <div className={styles.header}>
        <h3 className={styles.title}>{settings.title}</h3>
        {!emptyFormValues && (
          <button
            className={styles.resetButton}
            type="button"
            onClick={() => {
              setFormValues(settings.form.defaultState)
            }}
          >
            {settings.resetButtonLabel}
          </button>
        )}
        {collapsible && (
          <button
            type="button"
            onClick={() => setCollapsed(!collapsed)}
            className={styles.collapseButton}
          >
            <ArrowBird className={styles.collapseButtonIcon} />
          </button>
        )}
      </div>
      <div
        className={clsx(styles.box, {
          [styles.collapsibleBox]: collapsible,
          [styles.collapsed]: collapsed,
        })}
      >
        <form
          className={styles.form}
          ref={ref}
          onSubmit={e => {
            e.preventDefault()
            onSubmit(formValues)
          }}
        >
          <SplitterLine className={styles.splitterColor} />

          <div className={styles.inputsContainer}>
            {settings.form.fields.map(({ field, label }) => {
              if (field.type === 'select') {
                const fieldValue = formValues[field.name] as string | null
                return (
                  <FieldBox key={field.name} label={label}>
                    <FieldSelect
                      field={field}
                      onChange={value => {
                        setFormValues(prev => {
                          const result = {
                            ...prev,
                            [field.name]: value,
                          }

                          return result
                        })
                      }}
                      value={fieldValue ?? ''}
                    />
                  </FieldBox>
                )
              }

              if (field.type === 'slider') {
                const fieldValue = formValues[field.name] as { min: number; max: number } | null
                return (
                  <Fragment key={field.name}>
                    <SplitterLine className={clsx(styles.splitterColor, styles.splitterSlider)} />

                    <div className={styles.sliderBox}>
                      <h4 className={styles.sliderLabel}>{label}</h4>
                      <FieldSlider
                        renderMinDotLabel={v => (
                          <span className={styles.sliderDotLabel}>{v.min.toFixed(1)}</span>
                        )}
                        renderMaxDotLabel={v => (
                          <span className={styles.sliderDotLabel}>{v.max.toFixed(1)}</span>
                        )}
                        className={styles.slider}
                        field={field}
                        onChange={value => {
                          setFormValues(prev => ({
                            ...prev,
                            [field.name]: value,
                          }))
                        }}
                        value={{
                          min: fieldValue?.min ?? 0,
                          max: fieldValue?.max ?? 0,
                        }}
                      />
                    </div>
                  </Fragment>
                )
              }

              if (field.type === 'range') {
                const fieldValue = formValues[field.name] as { min: number; max: number } | null
                return (
                  <div key={field.name} className={styles.rangeBox}>
                    <FieldBox label={label}>
                      <FieldNumber
                        field={{
                          ...field.min.field,
                          max: Math.min(
                            field.max?.field?.max ?? Infinity,
                            fieldValue?.max ?? Infinity
                          ),
                        }}
                        onBlur={() => {
                          const currentValue = formValues[field.name] as {
                            min: number
                            max: number
                          } | null
                          if (!currentValue) {
                            return
                          }

                          const allowedMin = field.min.field.min ?? 0,
                            allowedMax = Math.min(currentValue.max, field.min.field.max ?? Infinity)

                          if (currentValue.min >= allowedMin && currentValue.min <= allowedMax) {
                            return
                          }

                          setFormValues(prev => ({
                            ...prev,
                            [field.name]: {
                              ...currentValue,
                              min: Math.max(allowedMin, Math.min(currentValue.min, allowedMax)),
                            },
                          }))
                        }}
                        onChange={value => {
                          const valNum = parseInt(value, 10)

                          setFormValues(prev => ({
                            ...prev,
                            [field.name]: {
                              max: fieldValue?.max ?? 0,
                              min: valNum,
                            },
                          }))
                        }}
                        value={fieldValue?.min?.toString() ?? ''}
                      />
                    </FieldBox>
                    <FieldBox label="&nbsp;">
                      <FieldNumber
                        field={{
                          ...field.max.field,
                          min: Math.max(
                            field.min?.field?.min ?? -Infinity,
                            fieldValue?.min ?? -Infinity
                          ),
                        }}
                        onBlur={() => {
                          const currentValue = formValues[field.name] as {
                            min: number
                            max: number
                          } | null
                          if (!currentValue) {
                            return
                          }

                          const allowedMin = Math.max(field.max.field.min ?? 0, currentValue.min),
                            allowedMax = field.max.field.max ?? Infinity

                          if (currentValue.max >= allowedMin && currentValue.max <= allowedMax) {
                            return
                          }

                          setFormValues(prev => ({
                            ...prev,
                            [field.name]: {
                              ...currentValue,
                              max: Math.max(allowedMin, Math.min(currentValue.max, allowedMax)),
                            },
                          }))
                        }}
                        onChange={value => {
                          const valNum = parseInt(value, 10)

                          setFormValues(prev => ({
                            ...prev,
                            [field.name]: {
                              min: fieldValue?.min ?? 0,
                              max: valNum,
                            },
                          }))
                        }}
                        value={fieldValue?.max?.toString() ?? ''}
                      />
                    </FieldBox>
                  </div>
                )
              }
              return null
            })}
          </div>

          <RWButton variant="secondary" type="submit" size="medium" className={styles.applyButton}>
            {settings.applyButtonLabel}
          </RWButton>
        </form>
      </div>
    </div>
  )
}
